import {
  EnforcementPlan,
  OutcomeSummary,
  OutcomesForParty,
} from "../../shared/Analytics";
import { ShownOutcome } from "../../shared/adjustedView";
import { Stage } from "../../shared/CaseConfiguration";

export interface JudgmentsWithSameNetResult {
  netResult: number;
  judgments: JudgmentDetails[];
  // the one group of settlements: its stages, shown when it is opened
  settlementStages?: StageOfSettlement[];
}

export type StageOfSettlement = {
  stage: Stage;
  probability: number;
  netResult: number;
};
export interface JudgmentDetails {
  probability: number;
  judgementValue: number;
  netResult: number;
  issuesWon: string[];
  issuesLost: string[];
  enforcement?: { worstAmount: number; costs: number };
  /** Handle for fetching this outcome's heavy details (ledger) on demand via the
   * getOutcomeDetails API — the details are no longer carried inline. */
  uuid: string;
  /** Position within OutcomesForParty.outcomes — identifies the outcome for
   * the dev-only ledger DAG view (ledgerItemsOf/ledgersOfOutcome). */
  outcomeIndex: number;
}

function extractDetails(
  judgement: OutcomeSummary,
  outcomeIndex: number,
): JudgmentDetails {
  const worstEnforcement: EnforcementPlan | undefined =
    // enforcements are sorted from worst to best result
    judgement.enforcementVariants[0];

  const judgmentDetails: JudgmentDetails = {
    ...judgement,
    judgementValue: judgement.value,
    outcomeIndex,
  };
  if (worstEnforcement !== undefined) {
    judgmentDetails.enforcement = {
      worstAmount: worstEnforcement.value,
      costs: worstEnforcement.costs.total,
    };
  }
  return judgmentDetails;
}

const weighted = (rows: { probability: number; netResult: number }[]) => {
  const probability = rows.reduce((sum, row) => sum + row.probability, 0);
  return {
    probability,
    netResult:
      rows.reduce((sum, row) => sum + row.probability * row.netResult, 0) /
      probability,
  };
};

/** All settled results as one group, at their average, with their stages. */
function settlementGroup(settled: ShownOutcome[]): JudgmentsWithSameNetResult {
  const stages = [...new Set(settled.map((o) => o.settledAt as Stage))].sort(
    (a, b) => a - b,
  );
  return {
    netResult: weighted(settled).netResult,
    judgments: settled.map((outcome, index) => extractDetails(outcome, index)),
    settlementStages: stages.map((stage) => ({
      stage,
      ...weighted(settled.filter((o) => o.settledAt === stage)),
    })),
  };
}

export function groupJudgmentsByNetResult({
  outcomes,
}: OutcomesForParty<ShownOutcome>): JudgmentsWithSameNetResult[] {
  const judgmentsGroupedByNetResult = new Map<
    number,
    JudgmentsWithSameNetResult
  >();
  const settled = (outcomes ?? []).filter((o) => o.settledAt !== undefined);
  for (const [outcomeIndex, judgement] of (outcomes ?? []).entries()) {
    if (judgement.settledAt !== undefined) continue;
    const { netResult } = judgement;
    const judgementDetails = extractDetails(judgement, outcomeIndex);

    // store details mapped to net result
    const judgmentGroup = judgmentsGroupedByNetResult.get(netResult);
    if (judgmentGroup === undefined) {
      // create a new one
      judgmentsGroupedByNetResult.set(netResult, {
        netResult,
        judgments: [judgementDetails],
      });
      continue;
    }
    // add to judgments
    judgmentGroup.judgments.push(judgementDetails);
  }

  // sort by net result
  return [
    ...judgmentsGroupedByNetResult.values(),
    ...(settled.length > 0 ? [settlementGroup(settled)] : []),
  ].sort((o1, o2) => o1.netResult - o2.netResult);
}
